import { Module, GetterTree } from "vuex";
import { GlobalState } from "../global";
import { login } from "@/api/auth";
import { STORAGE_KEY_ID_TOKEN, STORAGE_KEY_NAME } from "@/api/http";
import { ZnaRequestError } from "@/api/http";

export interface UserAccount {
  authenticated: boolean;
  username: string;
  fullName: string;
  privileges: string[];
}

export interface AuthState {
  userAccount: UserAccount;
  loginError: any;
}

const getUserAccountFromLocalStorage = () => {
  const userAccount: UserAccount = {
    authenticated: false,
    username: "",
    fullName: "",
    privileges: [],
  };

  const idTokenJsonString = localStorage.getItem(STORAGE_KEY_ID_TOKEN);
  if (idTokenJsonString) {
    const idToken = JSON.parse(idTokenJsonString);
    userAccount.authenticated = true;
    userAccount.username = idToken["cognito:username"];
    userAccount.fullName = localStorage.getItem(STORAGE_KEY_NAME) || "";
    userAccount.privileges = idToken["cognito:groups"];
  }
  return userAccount;
};

const authModule: Module<AuthState, GlobalState> = {
  namespaced: true,

  // initial state
  state() {
    return {
      userAccount: getUserAccountFromLocalStorage(),
      currentToken: localStorage.getItem("auth-login-token") || "",
      loginError: null,
    };
  },

  getters: {
    isAuthenticated: (state, getters, rootState, rootGetters) =>
      state.userAccount.authenticated || false,
    fullName: (state, getters, rootState, rootGetters) =>
      state.userAccount.fullName,
    isStudio: (state, getters, rootState, rootGetters) =>
      getters.isAuthenticated &&
      state.userAccount.username.startsWith("studio-"),
    isLoginFlow: (state, getters, rootState, rootGetters) => () =>
      new Date().getTime() -
        parseInt(localStorage.getItem("lastLoginFlowStart") || "0") <
      30000,
    backendFehlerAkzeptabel: (state, getters, rootState, rootGetters) => () => {
      const anzahlFehlerOkay =
        parseInt(localStorage.getItem("numFailedBackendRequests") || "0") < 3;
      if (!anzahlFehlerOkay) {
        return false;
      }
      const zeitfensterGueltig =
          new Date().getTime() -
            parseInt(
              localStorage.getItem("lastSuccessfulBackendRequest") || "0"
            ) <
          300000 /* 5 Minuten */;
      return zeitfensterGueltig;
    },
    darfTerminplanVonStudio: (state, getters, rootState, rootGetters) => (
      studioBezeichner: string
    ) =>
      getters.isAuthenticated &&
      state.userAccount.privileges.reduce(
        (bisher, current) =>
          bisher ||
          current
            .toLowerCase()
            .startsWith(`studio-${studioBezeichner}/terminplan/`),
        false
      ),
    darfDienstplanVonStudioLesen: (state, getters, rootState, rootGetters) => (
      studioBezeichner: string
    ) =>
      getters.isAuthenticated &&
      state.userAccount.privileges.reduce(
        (bisher, current) =>
          bisher ||
          current
            .toLowerCase()
            .startsWith(`studio-${studioBezeichner}/dienstplan/lesezugriff`),
        false
      ),
      darfDienstplanVonStudioSchreiben: (state, getters, rootState, rootGetters) => (
        studioBezeichner: string
      ) =>
        getters.isAuthenticated &&
        state.userAccount.privileges.reduce(
          (bisher, current) =>
            bisher ||
            current
              .toLowerCase()
              .startsWith(`studio-${studioBezeichner}/dienstplan/schreibzugriff`),
          false
        ),
    darfBuchhaltung: (state, getters, rootState, rootGetters) =>
      getters.isAuthenticated &&
      state.userAccount.privileges &&
      state.userAccount.privileges.includes("Buchhaltung"),
    darfBuchhaltungTagesabschluss: (state, getters, rootState, rootGetters) =>
      getters.isAuthenticated &&
      state.userAccount.privileges &&
      state.userAccount.privileges.includes("Buchhaltung/Tagesabschluss"),
    darfNotAus: (state, getters, rootState, rootGetters) =>
      getters.isAuthenticated &&
      state.userAccount.privileges &&
      state.userAccount.privileges.includes("Not-Aus"),
    darfTageAuswaehlen: (state, getters, rootState, rootGetters) =>
      getters.isAuthenticated &&
      state.userAccount.privileges &&
      state.userAccount.privileges.includes("Tage-Auswaehlen"),
    darfGeloeschteTermine: (state, getters, rootState, rootGetters) =>
      getters.isAuthenticated &&
      state.userAccount.privileges &&
      state.userAccount.privileges.includes("Geloeschte-Termine"),
    darfTageLoeschen: (state, getters, rootState, rootGetters) =>
      getters.isAuthenticated &&
      state.userAccount.privileges &&
      state.userAccount.privileges.includes("Buchhaltung/Loeschen"),
  },

  actions: {
    login(context, { scope, appComponentsData }) {
      context.commit("authFlowStarted");
      context.dispatch("appLoading", true, { root: true });
      login(scope, appComponentsData);
      /*
      login(scope).subscribe(
        (userAccountOrStatus) => {
          context.dispatch("appLoading", false, { root: true });
          if (userAccountOrStatus.isAxiosError) {
            context.commit("loggedIn", { undefined });
            context.commit("loginError", { error: userAccountOrStatus.message });
          } else {
            context.commit("loginError", { undefined });
            context.commit("loggedIn", { userAccountOrStatus });
          }
        },
        (error) => {
          context.dispatch("appLoading", false, { root: true });
          console.log(error);
          context.commit("loginError", { error: error.message });
        }
      );
      */
    },
    afterLogin(context) {
      const userAccount = getUserAccountFromLocalStorage();
      console.log("Login/-out");
      console.log(userAccount);
      context.commit("authFlowEnded");
      context.commit("loggedIn", { userAccount });
      context.commit("blockApp", !userAccount.authenticated, { root: true });
    },
    onAuthError(context, param: { error: ZnaRequestError }) {
      const numBackendFehler = parseInt(
        localStorage.getItem("numFailedBackendRequests") || "0"
      );
      localStorage.setItem(
        "numFailedBackendRequests",
        "" + (numBackendFehler + 1)
      );

      if (
        !param.error.isCognitoError &&
        context.getters["backendFehlerAkzeptabel"]()
      ) {
        return;
      }

      context.commit("blockApp", true, { root: true });
      context.commit("loggedIn", {
        userAccount: {
          authenticated: false,
          username: "",
          fullName: "",
          privileges: [],
        } as UserAccount,
      });
    },
    successfulBackendRequest(context) {
      context.commit("successfulBackendRequest", "" + new Date().getTime());
    },
  },

  mutations: {
    authFlowStarted(state) {
      console.log("Started Auth Flow Timer");
      localStorage.setItem("lastLoginFlowStart", "" + new Date().getTime());
    },
    authFlowEnded(state) {
      localStorage.removeItem("lastLoginFlowStart");
    },
    loggedIn(state, { userAccount }) {
      state.userAccount = userAccount;
    },
    loginError(state, { error }) {
      state.loginError = error;
    },
    successfulBackendRequest(state) {
      localStorage.setItem(
        "lastSuccessfulBackendRequest",
        "" + new Date().getTime()
      );
      localStorage.setItem("numFailedBackendRequests", "0");
    },
  },
};

export { authModule };
